/* Oakline — global scripts (no dependencies) */
(() => {
  const theme = (window.theme = window.theme || {});
  theme.strings = theme.strings || {};

  // Shopify's t filter HTML-escapes translations (don't → don&#39;t). Decode them once so scripts
  // can use the strings as plain text; anything written into innerHTML goes through theme.escapeHTML.
  const entityDecoder = document.createElement('textarea');
  Object.keys(theme.strings).forEach((key) => {
    entityDecoder.innerHTML = theme.strings[key];
    theme.strings[key] = entityDecoder.value;
  });

  /* ---------- Utilities ---------- */
  theme.escapeHTML = (value) =>
    String(value).replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);

  theme.debounce = (fn, wait = 300) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  theme.fetchConfig = (type = 'json') => ({
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  });

  theme.parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  theme.formatMoney = (cents, format) => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const formatString = format || theme.moneyFormat || '${{amount}}';
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const withDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      if (number === null || number === undefined || Number.isNaN(Number(number))) return '0';
      const fixed = (Number(number) / 100).toFixed(precision);
      const [whole, fraction] = fixed.split('.');
      return whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`) + (fraction ? decimal + fraction : '');
    };
    const match = formatString.match(placeholder);
    if (!match) return formatString;
    let value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(cents, 0, ' ');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        value = withDelimiters(cents, 2, ' ', '.');
        break;
      default:
        value = withDelimiters(cents, 2);
    }
    return formatString.replace(placeholder, value);
  };

  theme.toast = (message, type = 'info', duration = 4000) => {
    const toast = document.getElementById('ThemeToast');
    if (!toast) return;
    toast.textContent = message;
    toast.classList.toggle('toast--error', type === 'error');
    toast.hidden = false;
    clearTimeout(theme._toastTimer);
    theme._toastTimer = setTimeout(() => {
      toast.hidden = true;
    }, duration);
  };

  /* Scroll lock (reference counted so nested overlays behave) */
  let scrollLocks = 0;
  theme.lockScroll = () => {
    scrollLocks += 1;
    if (scrollLocks === 1) {
      const scrollbar = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.setProperty('--scrollbar-width', `${scrollbar}px`);
      document.documentElement.style.paddingRight = `${scrollbar}px`;
      document.documentElement.classList.add('overflow-hidden');
    }
  };
  theme.unlockScroll = () => {
    scrollLocks = Math.max(0, scrollLocks - 1);
    if (scrollLocks === 0) {
      document.documentElement.classList.remove('overflow-hidden');
      document.documentElement.style.paddingRight = '';
    }
  };

  /* Focus trap */
  const FOCUSABLE =
    'summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), input:not([type="hidden"]):enabled, select:enabled, textarea:enabled, iframe';
  const trapHandlers = new Map();
  theme.trapFocus = (container, elementToFocus) => {
    theme.removeTrapFocus();
    const handler = (event) => {
      if (event.key !== 'Tab') return;
      const nodes = [...container.querySelectorAll(FOCUSABLE)].filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handler);
    trapHandlers.set(container, handler);
    const target = elementToFocus || container.querySelector(FOCUSABLE) || container;
    requestAnimationFrame(() => target.focus({ preventScroll: true }));
  };
  theme.removeTrapFocus = (elementToFocus) => {
    trapHandlers.forEach((handler) => document.removeEventListener('keydown', handler));
    trapHandlers.clear();
    if (elementToFocus) elementToFocus.focus({ preventScroll: true });
  };

  /* ---------- Drawer base ---------- */
  class DrawerElement extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.drawer__panel, [data-drawer-panel]');
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]')) {
          event.preventDefault();
          this.close();
        }
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open(trigger) {
      if (this.isOpen) return;
      if (trigger) this.activeTrigger = trigger;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      if (this.activeTrigger) this.activeTrigger.setAttribute('aria-expanded', 'true');
      theme.lockScroll();
      theme.trapFocus(this.panel || this, this.querySelector('[data-autofocus]') || this.panel);
      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      if (this.activeTrigger) this.activeTrigger.setAttribute('aria-expanded', 'false');
      theme.unlockScroll();
      theme.removeTrapFocus(this.activeTrigger);
      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }
  }
  theme.DrawerElement = DrawerElement;
  if (!customElements.get('drawer-element')) customElements.define('drawer-element', DrawerElement);
  if (!customElements.get('menu-drawer')) customElements.define('menu-drawer', class extends DrawerElement {});

  /* Open any drawer/modal from a trigger: <button data-drawer-open="CartDrawer"> */
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-drawer-open]');
    if (!trigger) return;
    const target = document.getElementById(trigger.dataset.drawerOpen);
    if (target && typeof target.open === 'function') {
      event.preventDefault();
      target.open(trigger);
    }
  });

  /* ---------- Sticky header ---------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.sticky || 'none';
      this.transparent = this.hasAttribute('data-transparent');
      // Only see-through when the page starts with a full-width image (hero or collection banner)
      const firstSection = document.querySelector('#MainContent > .shopify-section');
      if (this.transparent && !(firstSection && firstSection.querySelector('.full-bleed'))) {
        this.removeAttribute('data-transparent');
        this.transparent = false;
      }
      this.lastScroll = window.scrollY;
      this.checkNavFit();
      this.setHeight();
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.resizeObserver = new ResizeObserver(() => this.setHeight());
      this.resizeObserver.observe(this);
      window.addEventListener('resize', theme.debounce(() => this.checkNavFit(), 150));
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.checkNavFit());
      this.addEventListener('mouseenter', () => this.transparent && this.classList.add('is-active'));
      this.addEventListener('mouseleave', () => {
        if (!this.querySelector('details[open]')) this.classList.remove('is-active');
      });
      this.addEventListener('focusin', () => this.transparent && this.classList.add('is-active'));
      this.onScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    /* Switch to the drawer menu when the desktop menu doesn't fit on one line */
    checkNavFit() {
      const header = this.querySelector('.site-header');
      const nav = this.querySelector('.site-nav');
      if (!header || !nav) return;
      header.classList.remove('is-compact');
      if (window.innerWidth < 990) return;
      const overflowing = nav.scrollWidth > nav.clientWidth + 2;
      header.classList.toggle('is-compact', overflowing);
    }

    setHeight() {
      const height = this.offsetHeight;
      document.documentElement.style.setProperty('--header-height', `${height}px`);
      document.documentElement.style.setProperty('--header-offset', this.transparent ? `${height}px` : '0px');
    }

    onScroll() {
      const current = window.scrollY;
      this.classList.toggle('is-scrolled', current > 8 && this.mode !== 'none');
      if (this.mode === 'on-scroll-up') {
        const goingDown = current > this.lastScroll;
        const beyond = current > this.offsetHeight * 2;
        const menuOpen = this.querySelector('details[open]');
        this.classList.toggle('is-hidden', goingDown && beyond && !menuOpen);
      }
      // Height of the header currently covering the page, used to offset other sticky elements
      const covering = this.mode !== 'none' && current > 0 && !this.classList.contains('is-hidden');
      document.documentElement.style.setProperty('--header-visible-height', covering ? `${this.offsetHeight}px` : '0px');
      this.lastScroll = Math.max(0, current);
    }
  }
  if (!customElements.get('sticky-header')) customElements.define('sticky-header', StickyHeader);

  /* ---------- Desktop menu (hover + click + keyboard) ---------- */
  class HeaderMenu extends HTMLElement {
    connectedCallback() {
      this.details = [...this.querySelectorAll(':scope details')];
      this.header = this.closest('sticky-header');
      const canHover = window.matchMedia('(hover: hover)').matches;
      this.details.forEach((detail) => {
        const item = detail.closest('.site-nav__item') || detail;
        detail.addEventListener('toggle', () => {
          if (detail.open) {
            this.details.forEach((other) => other !== detail && (other.open = false));
            if (this.header) this.header.classList.add('is-active');
          } else if (this.header && !this.details.some((d) => d.open)) {
            this.header.classList.remove('is-active');
          }
          const summary = detail.querySelector('summary');
          if (summary) summary.setAttribute('aria-expanded', detail.open);
        });
        if (canHover) {
          item.addEventListener('mouseenter', () => {
            clearTimeout(detail._closeTimer);
            detail.open = true;
          });
          item.addEventListener('mouseleave', () => {
            detail._closeTimer = setTimeout(() => (detail.open = false), 160);
          });
        }
      });
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        const open = this.details.find((d) => d.open);
        if (open) {
          open.open = false;
          open.querySelector('summary').focus();
        }
      });
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.details.forEach((d) => (d.open = false));
      });
    }
  }
  if (!customElements.get('header-menu')) customElements.define('header-menu', HeaderMenu);

  /* ---------- Search modal ---------- */
  class SearchModal extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.search-modal__panel');
      this.input = this.querySelector('input[type="search"]');
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-search-close]')) this.close();
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-search-open]');
        if (!trigger) return;
        event.preventDefault();
        this.open(trigger);
      });
    }

    open(trigger) {
      this.trigger = trigger;
      this.classList.add('is-open');
      theme.lockScroll();
      theme.trapFocus(this.panel, this.input);
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      theme.unlockScroll();
      theme.removeTrapFocus(this.trigger);
    }
  }
  if (!customElements.get('search-modal')) customElements.define('search-modal', SearchModal);

  /* ---------- Quantity input ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button[name]').forEach((button) =>
        button.addEventListener('click', (event) => {
          event.preventDefault();
          const previous = this.input.value;
          const step = Number(this.input.step) || 1;
          const min = Number(this.input.min) || 0;
          const max = this.input.max ? Number(this.input.max) : Infinity;
          const current = Number(this.input.value) || 0;
          const next = button.name === 'plus' ? current + step : current - step;
          this.input.value = Math.min(max, Math.max(min, next));
          if (previous !== this.input.value) this.input.dispatchEvent(new Event('change', { bubbles: true }));
        })
      );
    }
  }
  if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

  /* ---------- Slider / carousel ---------- */
  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slider]');
      if (!this.track) return;
      this.prev = this.querySelector('[data-slider-prev]');
      this.next = this.querySelector('[data-slider-next]');
      this.dots = [...this.querySelectorAll('[data-slider-dot]')];
      if (this.prev) this.prev.addEventListener('click', () => this.go(-1));
      if (this.next) this.next.addEventListener('click', () => this.go(1));
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.goTo(index)));
      this.update = this.update.bind(this);
      this.track.addEventListener('scroll', theme.debounce(this.update, 60), { passive: true });
      window.addEventListener('resize', theme.debounce(this.update, 120));
      if (this.querySelector('.slider-button--side') && 'ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(theme.debounce(this.update, 60));
        this.resizeObserver.observe(this.track);
      }
      this.update();
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    /* Side arrows sit on the vertical middle of the product images.
       Layout offsets are used so reveal animations (transforms) don't shift the result. */
    positionArrows() {
      const slide = this.slides[0];
      const media = slide && slide.querySelector('.card__media, .media');
      if (!media) return;
      let top = media.offsetHeight / 2;
      let el = media;
      while (el && el !== this) {
        top += el.offsetTop;
        el = el.offsetParent;
      }
      if (el === this) this.style.setProperty('--slider-arrow-top', `${Math.round(top)}px`);
    }

    get slides() {
      return [...this.track.children].filter((el) => el.offsetParent !== null);
    }

    go(direction) {
      const amount = this.track.clientWidth * 0.9;
      this.track.scrollBy({ left: direction * amount, behavior: 'smooth' });
    }

    goTo(index) {
      const slide = this.slides[index];
      if (!slide) return;
      this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft - parseFloat(getComputedStyle(this.track).paddingLeft || 0), behavior: 'smooth' });
    }

    update() {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      if (this.prev) this.prev.disabled = scrollLeft <= 4;
      if (this.next) this.next.disabled = scrollLeft + clientWidth >= scrollWidth - 4;
      const controls = this.querySelector('.slider-controls');
      if (controls) controls.hidden = scrollWidth <= clientWidth + 4;
      this.classList.toggle('has-overflow', scrollWidth > clientWidth + 4);
      if (this.querySelector('.slider-button--side')) this.positionArrows();
      if (this.dots.length) {
        const slides = this.slides;
        let active = 0;
        slides.forEach((slide, index) => {
          if (slide.offsetLeft - this.track.offsetLeft <= scrollLeft + 10) active = index;
        });
        if (scrollLeft + clientWidth >= scrollWidth - 4) active = slides.length - 1;
        this.dots.forEach((dot, index) => dot.setAttribute('aria-current', index === active ? 'true' : 'false'));
      }
    }
  }
  if (!customElements.get('slider-component')) customElements.define('slider-component', SliderComponent);

  /* ---------- Announcement bar ---------- */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.messages = [...this.querySelectorAll('.announcement-bar__message')];
      this.index = 0;
      this.delay = Number(this.dataset.speed || 5) * 1000;
      if (this.messages.length < 2) return;
      this.querySelector('[data-announcement-prev]')?.addEventListener('click', () => this.show(this.index - 1, true));
      this.querySelector('[data-announcement-next]')?.addEventListener('click', () => this.show(this.index + 1, true));
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      document.addEventListener('shopify:block:select', (event) => {
        const index = this.messages.findIndex((m) => m.contains(event.target) || m === event.target);
        if (index > -1) {
          this.stop();
          this.show(index);
        }
      });
      this.start();
    }

    disconnectedCallback() {
      this.stop();
    }

    start() {
      if (this.dataset.autoplay !== 'true') return;
      this.stop();
      this.timer = setInterval(() => this.show(this.index + 1), this.delay);
    }

    stop() {
      clearInterval(this.timer);
    }

    show(index, userInitiated) {
      const total = this.messages.length;
      this.index = (index + total) % total;
      this.messages.forEach((message, i) => {
        message.classList.toggle('is-active', i === this.index);
        message.setAttribute('aria-hidden', i === this.index ? 'false' : 'true');
        message.tabIndex = i === this.index ? 0 : -1;
      });
      if (userInitiated) this.start();
    }
  }
  if (!customElements.get('announcement-bar')) customElements.define('announcement-bar', AnnouncementBar);

  /* ---------- Localization (country / language) ---------- */
  class LocalizationForm extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('select').forEach((select) =>
        select.addEventListener('change', () => this.querySelector('form').submit())
      );
    }
  }
  if (!customElements.get('localization-form')) customElements.define('localization-form', LocalizationForm);

  /* ---------- Reveal on scroll ---------- */
  theme.initReveal = (root = document) => {
    if (!document.body.classList.contains('has-reveal')) return;
    const targets = root.querySelectorAll('[data-reveal]:not(.is-revealed)');
    if (!('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    if (!theme._revealObserver) {
      theme._revealObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-revealed');
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      );
    }
    targets.forEach((el) => theme._revealObserver.observe(el));
  };

  document.addEventListener('DOMContentLoaded', () => theme.initReveal());
  document.addEventListener('shopify:section:load', (event) => theme.initReveal(event.target));

  /* ---------- Scrolling image with text ---------- */
  class ImageScroll extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('.image-scroll__item')];
      this.images = [...this.querySelectorAll('.image-scroll__image')];
      this.addEventListener('shopify:block:select', (event) => {
        const index = this.items.indexOf(event.target);
        if (index > -1) this.activate(index);
      });
      if (!('IntersectionObserver' in window) || this.items.length < 2) return;
      // The item crossing the middle of the screen is the active one
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.activate(this.items.indexOf(entry.target));
          });
        },
        { rootMargin: '-45% 0px -45% 0px' }
      );
      this.items.forEach((item) => this.observer.observe(item));
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    activate(index) {
      this.items.forEach((item, i) => item.classList.toggle('is-active', i === index));
      this.images.forEach((image, i) => image.classList.toggle('is-active', i === index));
    }
  }
  if (!customElements.get('image-scroll')) customElements.define('image-scroll', ImageScroll);

  /* ---------- Scroll to the next section (hero scroll-down button) ---------- */
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-scroll-next]');
    if (!button) return;
    const section = button.closest('.shopify-section');
    const next = section && section.nextElementSibling;
    if (next) next.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
})();
